import { useMemo } from 'react';
import { useAppState } from '@/store';
import { selectAdminProfile } from '@/modules/AdminProfile';
import { AccessLevel, Resource } from '../constants';

export interface UsePermissionsResult {
  permissions: string[];
  /** Raw permission check, e.g. `can('partner.login_as')`. */
  can: (permission: string) => boolean;
  /** True if the resource is visible (view or editor level). */
  canView: (resource: Resource) => boolean;
  /** True if the resource is editable. */
  canManage: (resource: Resource) => boolean;
  /** Three-level access for a resource. */
  accessLevel: (resource: Resource) => AccessLevel;
}

/**
 * Permission-driven access control. Reads the effective permission list provided
 * by the backend (`/profile.permissions`) — super admin receives the full catalog.
 */
export const usePermissions = (): UsePermissionsResult => {
  const profile = useAppState(selectAdminProfile);

  return useMemo<UsePermissionsResult>(() => {
    const permissions = profile?.permissions ?? [];
    const has = (permission: string): boolean => permissions.includes(permission);
    const canManage = (resource: Resource): boolean => has(`${resource}.manage`);
    const canView = (resource: Resource): boolean => has(`${resource}.view`) || canManage(resource);
    const accessLevel = (resource: Resource): AccessLevel => {
      if (canManage(resource)) return 'editor';
      if (canView(resource)) return 'view';

      return 'closed';
    };

    return { permissions, can: has, canView, canManage, accessLevel };
  }, [profile]);
};
